import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCaptcha } from '../../../hooks/useCaptcha';

/**
 * Состояние публичного AI-чата лендинга (поверхность S6).
 *
 * Контракт: GET /api/assistant/widget/landing-bootstrap (публичный, всегда 200)
 * → widget token ООО платформы → POST /api/assistant/widget/chat с Bearer этого токена.
 *
 * Здесь намеренно используется fetch, а не utils/axios: interceptors общего клиента
 * подставляют пользовательский uam-токен, а widget-контур должен идти только со своим
 * коротким widget token (ключи проекта в браузер не попадают).
 */

const BOOTSTRAP_URL = '/api/assistant/widget/landing-bootstrap';
const CHAT_URL = '/api/assistant/widget/chat';
/** Ре-bootstrap, когда израсходовано 80% TTL токена. */
const TOKEN_REFRESH_RATIO = 0.8;
const DEFAULT_GUEST_MESSAGE_LIMIT = 3;
const MAX_INPUT_LENGTH = 1200;

export const LANDING_AGENT_OPEN_EVENT = 'mb-landing-open-agent';
/** Единственная точка открытия чата: hero, карточки ai-flow, лаунчер шапки. */
export function openLandingAgent(prompt) {
  if (typeof window === 'undefined') return;
  const detail = typeof prompt === 'string' && prompt.trim() ? { prompt: prompt.trim() } : {};
  window.dispatchEvent(new CustomEvent(LANDING_AGENT_OPEN_EVENT, { detail }));
}

/** Строка лаунчера до ответа bootstrap и при недоступной сети — без ротации, высота не меняется. */
export const LAUNCHER_FALLBACK_PLACEHOLDER =
  'Спросите AI: соберу витрину, подберу тариф, перенесу товары';

const ERROR_TEXT = {
  auth: 'Чат сейчас недоступен. Задачу можно решить в кабинете — бесплатный тариф открыт сразу после регистрации.',
  rate: 'Слишком много запросов подряд. Подождите минуту и продолжим.',
  generic: 'Не получилось получить ответ. Попробуйте ещё раз или напишите нам — ответим лично.',
  captcha: 'Проверка безопасности не завершена. Вопрос сохранён — попробуйте отправить ещё раз.',
};

const EMPTY_PRESENTATION = {
  ready: false,
  enabled: false,
  suggestions: [],
  placeholders: [],
  onboardingText: '',
  guestMessageLimit: DEFAULT_GUEST_MESSAGE_LIMIT,
  attachEnabled: false,
};

let messageSeq = 0;
const nextMessageId = () => {
  messageSeq += 1;
  return `m${messageSeq}`;
};

const asText = (value, maxLength = 4000) =>
  typeof value === 'string' ? value.trim().slice(0, maxLength) : '';

function normalizeBootstrap(raw) {
  const suggestions = Array.isArray(raw?.suggestions)
    ? raw.suggestions
        .map((item, index) => {
          const label = asText(item?.label, 120);
          if (!label) return null;
          return {
            id: asText(item?.id, 40) || `s${index + 1}`,
            label,
            prompt: asText(item?.prompt, 600) || label,
          };
        })
        .filter(Boolean)
        .slice(0, 8)
    : [];

  const placeholders = Array.isArray(raw?.launcher_placeholders)
    ? raw.launcher_placeholders.map((item) => asText(item, 120)).filter(Boolean).slice(0, 8)
    : [];

  const rawLimit = Number(raw?.guest_message_limit);
  const guestMessageLimit = Number.isFinite(rawLimit)
    ? Math.min(10, Math.max(1, Math.trunc(rawLimit)))
    : DEFAULT_GUEST_MESSAGE_LIMIT;

  const expiresIn = Number(raw?.expires_in);

  return {
    presentation: {
      ready: true,
      enabled: raw?.enabled === true,
      suggestions,
      placeholders,
      onboardingText: asText(raw?.onboarding_text, 600),
      guestMessageLimit,
      attachEnabled: raw?.attach_enabled === true,
    },
    token: asText(raw?.widget_token, 4096) || null,
    tokenType: asText(raw?.token_type, 20) || 'Bearer',
    expiresIn: Number.isFinite(expiresIn) && expiresIn > 0 ? Math.trunc(expiresIn) : 0,
  };
}

function extractAssistantText(payload) {
  const candidates = [
    payload?.choices?.[0]?.message?.content,
    payload?.choices?.[0]?.delta?.content,
    payload?.message?.content,
    payload?.content,
    payload?.answer,
  ];
  for (const candidate of candidates) {
    const text = asText(candidate);
    if (text) return text;
  }
  return '';
}

export default function useLandingAgentChat() {
  const [presentation, setPresentation] = useState(EMPTY_PRESENTATION);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const { executeCaptcha, resetCaptcha, isExecuting: captchaPending } = useCaptcha({
    action: 'widget_chat', autoInit: false,
  });

  const mountedRef = useRef(true);
  const tokenRef = useRef(null);
  const bootstrapPromiseRef = useRef(null);
  const sendLockRef = useRef(false);
  const historyRef = useRef([]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isTokenFresh = useCallback(() => {
    const entry = tokenRef.current;
    if (!entry?.token) return false;
    if (!entry.expiresIn) return true;
    return Date.now() < entry.fetchedAt + entry.expiresIn * 1000 * TOKEN_REFRESH_RATIO;
  }, []);

  /** Single-flight: параллельные вызовы получают один и тот же запрос. */
  const ensureBootstrap = useCallback(({ force = false } = {}) => {
    if (bootstrapPromiseRef.current && !force) return bootstrapPromiseRef.current;

    const promise = (async () => {
      const response = await fetch(BOOTSTRAP_URL, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'omit',
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('bootstrap_unavailable');
      const normalized = normalizeBootstrap(await response.json());
      tokenRef.current = normalized.token
        ? {
            token: normalized.token,
            tokenType: normalized.tokenType,
            expiresIn: normalized.expiresIn,
            fetchedAt: Date.now(),
          }
        : null;
      if (mountedRef.current) setPresentation(normalized.presentation);
      return normalized;
    })();

    bootstrapPromiseRef.current = promise;
    promise
      .catch(() => {
        if (mountedRef.current) {
          setPresentation((prev) => (prev.ready ? prev : { ...EMPTY_PRESENTATION, ready: true }));
        }
      })
      .finally(() => {
        if (bootstrapPromiseRef.current === promise) bootstrapPromiseRef.current = null;
      });

    return promise;
  }, []);

  const resolveToken = useCallback(
    async ({ force = false } = {}) => {
      if (!force && isTokenFresh()) return tokenRef.current;
      try {
        await ensureBootstrap({ force: true });
      } catch (_) {
        return null;
      }
      return tokenRef.current?.token ? tokenRef.current : null;
    },
    [ensureBootstrap, isTokenFresh]
  );

  const userMessageCount = useMemo(
    () => messages.filter((message) => message.role === 'assistant' && !message.pending && !message.failed).length,
    [messages]
  );
  const limitReached = presentation.ready && userMessageCount >= presentation.guestMessageLimit;

  const postChat = useCallback(async (tokenEntry, apiMessages, captchaProof = null) => {
    const tokenType = tokenEntry.tokenType || 'Bearer';
    return fetch(CHAT_URL, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `${tokenType} ${tokenEntry.token}`,
      },
      body: JSON.stringify({ ...captchaProof, model: 'auto', stream: false, messages: apiMessages }),
    });
  }, []);

  const sendMessage = useCallback(
    async (rawText) => {
      const text = asText(rawText, MAX_INPUT_LENGTH);
      if (!text || sendLockRef.current || limitReached) return false;

      sendLockRef.current = true;
      setSending(true);

      const assistantId = nextMessageId();
      const apiMessages = [...historyRef.current, { role: 'user', content: text }];
      historyRef.current = apiMessages;

      setMessages((prev) => [
        ...prev,
        { id: nextMessageId(), role: 'user', content: text },
        { id: assistantId, role: 'assistant', content: '', pending: true },
      ]);

      const finish = (content, failed = false) => {
        if (!mountedRef.current) return;
        setMessages((prev) =>
          prev.map((message) =>
            message.id === assistantId ? { ...message, content, pending: false, failed } : message
          )
        );
      };

      try {
        let tokenEntry = await resolveToken();
        if (!tokenEntry) {
          historyRef.current = historyRef.current.slice(0, -1);
          finish(ERROR_TEXT.auth, true);
          return false;
        }

        let response = await postChat(tokenEntry, apiMessages);
        if (response.status === 401 || response.status === 403) {
          tokenEntry = await resolveToken({ force: true });
          response = tokenEntry ? await postChat(tokenEntry, apiMessages) : response;
        }

        let payload = await response.json().catch(() => null);
        // The server decides adaptively whether this caller needs a proof.
        // Never bypass its decision or forward a synthetic "verified" token.
        if (response.status === 400 && payload?.code === 'CAPTCHA_REQUIRED' && payload?.captcha_required === true) {
          const proof = await executeCaptcha();
          if (!proof || proof.captcha_unavailable) {
            historyRef.current = historyRef.current.slice(0, -1);
            finish(ERROR_TEXT.captcha, true);
            return false;
          }
          // A human challenge may outlive the short widget-token TTL.
          tokenEntry = await resolveToken();
          if (!tokenEntry) {
            historyRef.current = historyRef.current.slice(0, -1);
            finish(ERROR_TEXT.auth, true);
            return false;
          }
          try {
            response = await postChat(tokenEntry, apiMessages, proof);
            payload = await response.json().catch(() => null);
          } finally {
            resetCaptcha();
          }
          // Proofs are single-use. An auth failure here must not resend one.
        }

        if (!response.ok) {
          historyRef.current = historyRef.current.slice(0, -1);
          if (response.status === 429) finish(ERROR_TEXT.rate, true);
          else if (response.status === 401 || response.status === 403) finish(ERROR_TEXT.auth, true);
          else if (payload?.captcha_required || payload?.captcha_unavailable || payload?.code === 'CAPTCHA_FAILED') finish(ERROR_TEXT.captcha, true);
          else finish(ERROR_TEXT.generic, true);
          return false;
        }

        const answer = extractAssistantText(payload);
        if (!answer) {
          historyRef.current = historyRef.current.slice(0, -1);
          finish(ERROR_TEXT.generic, true);
          return false;
        }
        historyRef.current = [...apiMessages, { role: 'assistant', content: answer }];
        finish(answer);
        return true;
      } catch (_) {
        historyRef.current = historyRef.current.slice(0, -1);
        finish(ERROR_TEXT.generic, true);
        return false;
      } finally {
        sendLockRef.current = false;
        if (mountedRef.current) setSending(false);
      }
    },
    [limitReached, postChat, resolveToken, executeCaptcha, resetCaptcha]
  );

  return {
    ...presentation,
    messages,
    sending,
    captchaPending,
    userMessageCount,
    limitReached,
    maxInputLength: MAX_INPUT_LENGTH,
    ensureBootstrap,
    sendMessage,
  };
}
